import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaLightbulb, 
  FaUsers, 
  FaAward, 
  FaHandshake,
  FaRocket,
  FaShieldAlt,
  FaChartLine,
  FaHeart,
  FaLinkedin
} from 'react-icons/fa';

const About = () => {
  const values = [
    {
      icon: <FaLightbulb />,
      title: "Innovation",
      description: "Constantly pushing the boundaries of what's possible with AI technology"
    },
    {
      icon: <FaUsers />,
      title: "Collaboration",
      description: "Working closely with clients to understand and solve their unique challenges"
    },
    {
      icon: <FaAward />,
      title: "Excellence",
      description: "Delivering the highest quality solutions with attention to every detail"
    },
    {
      icon: <FaHandshake />,
      title: "Trust",
      description: "Building long-term partnerships based on transparency and reliability"
    }
  ];

  const team = [
    {
      name: "Nolan Crook",
      role: "Owner/Operator",
      description: "Software developer with experience in AI and web development, helping businesses with their technology needs",
      expertise: ["AI/ML", "Web Development", "Cloud Services"]
    }
  ];

  const advisors = [
    {
      name: "Tyler Walter",
      linkedIn: "https://www.linkedin.com/in/tylermwalter/",
      description: "Data Engineering and Cloud Engineering expert at Paradigm with extensive AWS expertise",
      expertise: ["Data Engineering", "Cloud Architecture", "AWS Certifications", "Machine Learning"]
    },
    {
      name: "Kevin Crook",
      linkedIn: "https://www.linkedin.com/in/kevin-crook-74b98810b/",
      description: "Seasoned professional with deep industry knowledge and strategic insight",
      expertise: ["Strategic Planning", "Industry Expertise", "Business Development"]
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section text-white" style={{ minHeight: '50vh' }}>
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={8} className="mx-auto text-center hero-content">
              <h1 className="display-4 fw-bold mb-4">About Stratis AI</h1>
              <p className="lead mb-5 fs-5">
                Founded by an AI engineer passionate about transforming businesses 
                through intelligent technology solutions.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="section-padding">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <h2 className="section-title">Our Mission</h2>
              <p className="section-subtitle">
                To democratize AI technology and make advanced machine learning solutions 
                accessible to businesses of all sizes.
              </p>
              <p className="mb-4">
                At Stratis AI, we believe that artificial intelligence should be a powerful 
                tool that drives innovation and growth for every organization. Our mission 
                is to bridge the gap between cutting-edge AI research and practical business 
                applications.
              </p>
              <p className="mb-4">
                We work closely with our clients to understand their unique challenges and 
                develop customized AI solutions that deliver measurable results. From 
                predictive analytics to intelligent automation, we help businesses leverage 
                the full potential of AI technology.
              </p>
              <Button 
                as={Link} 
                to="/services" 
                variant="primary" 
                size="lg"
                className="w-100 w-sm-auto"
              >
                Explore Our Services
              </Button>
            </Col>
            <Col lg={6}>
              <div className="bg-primary bg-opacity-10 p-4 p-lg-5 rounded-3">
                <h3 className="fw-bold mb-4">What We Do</h3>
                <div className="row">
                  <Col sm={6} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <FaRocket className="text-primary me-3" size={24} />
                      <div>
                        <h6 className="fw-bold mb-1">Innovation</h6>
                        <small className="text-muted">Cutting-edge AI solutions</small>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <FaShieldAlt className="text-primary me-3" size={24} />
                      <div>
                        <h6 className="fw-bold mb-1">Security</h6>
                        <small className="text-muted">Enterprise-grade protection</small>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <FaChartLine className="text-primary me-3" size={24} />
                      <div>
                        <h6 className="fw-bold mb-1">Growth</h6>
                        <small className="text-muted">Scalable solutions</small>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <FaHeart className="text-primary me-3" size={24} />
                      <div>
                        <h6 className="fw-bold mb-1">Support</h6>
                        <small className="text-muted">24/7 assistance</small>
                      </div>
                    </div>
                  </Col>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Our Values</h2>
              <p className="section-subtitle">
                The principles that guide everything we do at Stratis AI
              </p>
            </Col>
          </Row>
          <Row>
            {values.map((value, index) => (
              <Col lg={3} md={6} key={index} className="mb-4">
                <Card className="h-100 text-center border-0 bg-transparent">
                  <Card.Body className="p-4">
                    <div className="service-icon mb-3">
                      {value.icon}
                    </div>
                    <Card.Title className="fw-bold mb-3">{value.title}</Card.Title>
                    <Card.Text className="text-muted">
                      {value.description}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Advisors Section */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Our Advisors</h2>
              <p className="section-subtitle">
                Industry experts and thought leaders who guide our strategic vision
              </p>
            </Col>
          </Row>
          <Row>
            {advisors.map((advisor, index) => (
              <Col lg={6} key={index} className="mb-4">
                <Card className="h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start mb-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" 
                           style={{ width: '60px', height: '60px' }}>
                        <span className="fw-bold text-primary">{advisor.name.split(' ').map(n => n[0]).join('')}</span>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <Card.Title className="fw-bold mb-0 me-2">{advisor.name}</Card.Title>
                          <a 
                            href={advisor.linkedIn} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            <FaLinkedin size={18} />
                          </a>
                        </div>
                        <Card.Subtitle className="text-primary mb-2">Advisor</Card.Subtitle>
                        <Card.Text className="text-muted mb-3">
                          {advisor.description}
                        </Card.Text>
                        <div className="d-flex flex-wrap">
                          {advisor.expertise.map((skill, skillIndex) => (
                            <Badge 
                              key={skillIndex} 
                              bg="light" 
                              text="dark" 
                              className="me-2 mb-1"
                            >
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Meet the Founder</h2>
              <p className="section-subtitle">
                Get to know the person behind Stratis AI's innovative solutions
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            {team.map((member, index) => (
              <Col lg={8} md={10} key={index} className="mb-4">
                <Card className="h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start mb-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" 
                           style={{ width: '60px', height: '60px' }}>
                        <span className="fw-bold text-primary">{member.name.split(' ').map(n => n[0]).join('')}</span>
                      </div>
                      <div className="flex-grow-1">
                        <Card.Title className="fw-bold mb-1">{member.name}</Card.Title>
                        <Card.Subtitle className="text-primary mb-2">{member.role}</Card.Subtitle>
                        <Card.Text className="text-muted mb-3">
                          {member.description}
                        </Card.Text>
                        <div className="d-flex flex-wrap">
                          {member.expertise.map((skill, skillIndex) => (
                            <Badge 
                              key={skillIndex} 
                              bg="light" 
                              text="dark" 
                              className="me-2 mb-1"
                            >
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title text-white">Ready to Work With Us?</h2>
              <p className="section-subtitle text-white-50">
                Let's discuss how our team can help transform your business with AI.
              </p>
              <Button 
                as={Link} 
                to="/contact" 
                variant="light" 
                size="lg" 
                className="px-4 py-3 fw-semibold"
              >
                Get in Touch
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default About;